import Badge from "@/components/Badge";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown";
import Input from "@/components/Input";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import BodyColumn from "@/components/Table/table-body/BodyColumn";
import BodyImageWithInfoColumn from "@/components/Table/table-body/BodyImageWithInfoColumn";
import HeaderColumn from "@/components/Table/table-header/HeaderColumn";
import Toogle from "@/components/Toogle";
import { ReactComponent as EYE_ICON } from "@/assets/icons/eye.svg";
import { ReactComponent as EDIT_ICON } from "@/assets/icons/edit-black.svg";
import { ReactComponent as TRASH_ICON } from "@/assets/icons/trash.svg";
import PROFILE_PIC from "@/assets/icons/no-image-square.svg";
import { useNavigate } from "react-router-dom";
import { ChangeEvent, useEffect, useState } from "react";
import { Product } from "@/@core/entity/product.entity";
import getProductInteractor from "@/@core/interactor/product/get-product";
import { OptionItem } from "@/components/Dropdown/model";
import Modal from "@/components/Modal";
import {
  BASE_URL,
  DROPDOWN_CATEGORY_PRODUCT_OPTIONS,
  DROPDOWN_STATUS_OPTIONS,
  STATUS_PRODUCT,
} from "@/utils/constant";
import deleteProductInteractor from "@/@core/interactor/product/delete-product";
import updateProductInteractor from "@/@core/interactor/product/update-product";
import toast, { Toaster } from "react-hot-toast";
import { formatPrice } from "@/utils/number";

let debounceSearch: any;

const TableSection = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState<string>("");
  const [category, setCategory] = useState<OptionItem>(
    DROPDOWN_CATEGORY_PRODUCT_OPTIONS[0]
  );
  const [status, setStatus] = useState<OptionItem>(DROPDOWN_STATUS_OPTIONS[0]);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [isShowModalDelete, setIsShowModalDelete] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<any>({});

  const getProducts = async (page = activePage, search: string = "") => {
    try {
      const response = await getProductInteractor({
        limit: 10,
        page,
        search,
        category: category.value,
        status: status.value,
        with_media: 1
      });

      const {
        data: { data, pagination },
      } = response;
      setProducts(data);

      if (pagination) {
        setActivePage(pagination.current_page);
        setTotalPage(pagination.last_page);
      }
    } catch (err) {
      console.error(err);
      setProducts([]);
      setActivePage(1);
      setTotalPage(1);
    }
  };

  const deleteProduct = async () => {
    try {
      const { id, name } = selectedProduct;
      await deleteProductInteractor(id);

      toast.success(`${name} has been deleted`);
      handleToggleModalDelete();
      getProducts(1);
    } catch (err) {
      console.error(err);
    }
  };

  const updateStatus = async (data: Product) => {
    try {
      const {
        id,
        name,
        category,
        size,
        master_sku,
        season,
        colour,
        price,
        description,
        images,
        status,
      } = data;
      const body = {
        name,
        category,
        size,
        master_sku,
        season,
        colour,
        price,
        status: status === 1 ? 2 : 1,
        description,
        media_ids: images.map((item: any) => item.id),
      };

      await updateProductInteractor(body, String(id));
      getProducts(activePage);
    } catch (err) {
      console.error(err);
    }
  };

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);

    clearTimeout(debounceSearch);
    debounceSearch = setTimeout(() => {
      getProducts(1, val);
    }, 500);
  };

  const onSearchEnter = (e: any) => {
    const keyCode = e.keyCode;
    const val = e.target.value;

    if (keyCode === 13) {
      clearTimeout(debounceSearch);
      getProducts(1, val);
    }
  };

  const onChangeCategory = (e: any) => {
    setCategory(e);
    setActivePage(1);
  };

  const onChageStatus = (e: any) => {
    setStatus(e);
    setActivePage(1);
  };

  const resetFilter = () => {
    setSearch("");
    setStatus(DROPDOWN_STATUS_OPTIONS[0]);
    setCategory(DROPDOWN_CATEGORY_PRODUCT_OPTIONS[0]);
    setActivePage(1);
  };

  const onChangePagination = (e: any) => {
    setActivePage(e);
  };

  const handleToggleModalDelete = (item?: any) => {
    if (item) {
      setIsShowModalDelete(true);
      setSelectedProduct(item);
    } else {
      setIsShowModalDelete(false);
      setSelectedProduct({});
    }
  };

  useEffect(() => {
    getProducts(activePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, category.value, status.value]);

  return (
    <>
      <div className="table-control-section">
        <div>
          <label>Search for product</label>
          <Input
            style={{ width: "360px" }}
            value={search}
            withIcon
            type="search"
            placeholder="Search"
            onChange={onChangeSearch}
            onKeyDown={onSearchEnter}
          />
        </div>
        <div>
          <label>Status</label>
          <Dropdown
            value={status}
            options={DROPDOWN_STATUS_OPTIONS}
            onChange={onChageStatus}
            optionType="text-option"
            width="192px"
          />
        </div>
        <div>
          <label>Category</label>
          <Dropdown
            value={category}
            options={DROPDOWN_CATEGORY_PRODUCT_OPTIONS}
            onChange={onChangeCategory}
            optionType="text-option"
            width="192px"
          />
        </div>

        <div className="btn-wrapper">
          <Button
            onClick={resetFilter}
            label="Reset Filter"
            type="secondary"
            size="md"
            disabled={!search && category.value === "all" && status.value === 0}
          />
        </div>
      </div>

      <div className="table-section">
        <Table>
          <thead>
            <tr>
              <HeaderColumn label="Product ID" alignLabel="text-left" />
              <HeaderColumn label="Product" alignLabel="text-left" />
              <HeaderColumn label="Master SKU" alignLabel="text-center" />
              <HeaderColumn label="Status" alignLabel="text-center" />
              <HeaderColumn label="Categories" alignLabel="text-center" />
              <HeaderColumn label="" alignLabel="text-left" />
            </tr>
          </thead>
          <tbody>
            {products.map((item, idx) => (
              <tr key={idx}>
                <td width="15%">
                  <div className="text-with-toogle-column">
                    <Toogle
                      checked={item.status === 1}
                      onChange={() => updateStatus(item)}
                    />
                    <span>{item.id}</span>
                  </div>
                </td>
                <BodyImageWithInfoColumn
                  width="300px"
                  imgSrc={item.images ? BASE_URL + "/file" + item.images[0]?.path : null}
                  roundedImg="20px"
                  textName={item.name}
                  defaultImg={PROFILE_PIC}
                  textDesc={`${formatPrice(
                    String(item.total_claimed),
                    ","
                  )} Product Claimed`}
                />
                <BodyColumn
                  width="200px"
                  label={item.master_sku}
                  alignLabel="text-center"
                />
                <td width="100px">
                  <div className="item-center">
                    <Badge
                      label={STATUS_PRODUCT[item.status]}
                      labelColor={item.status === 1 ? "#067647" : "#344054"}
                      backgroundColor={
                        item.status === 1 ? "#ECFDF3" : "#F2F4F7"
                      }
                    />
                  </div>
                </td>
                <td width="100px">
                  <div className="item-center">
                    <Badge
                      label={item.category}
                      labelColor="#344054"
                      backgroundColor="#F2F4F7"
                    />
                  </div>
                </td>
                <td width="20%">
                  <div className="cta-wrapper">
                    <button
                      onClick={() =>
                        navigate(`/products/product-detail/${item.id}`)
                      }
                    >
                      <EYE_ICON />
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/products/edit-product/${item.id}`)
                      }
                    >
                      <EDIT_ICON />
                    </button>
                    <button onClick={() => handleToggleModalDelete(item)}>
                      <TRASH_ICON />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination
          activePage={activePage}
          totalPage={totalPage}
          onChange={onChangePagination}
        />

        {isShowModalDelete && (
          <div className="delete-product-modal-wrapper">
            <Modal
              header={"header"}
              content={
                <div className="content-delete-product">
                  <h2 className="modal-title">Delete Product</h2>
                  <span className="desc">
                    Are you sure you want to delete this product? This action
                    cannot be undone.
                  </span>
                </div>
              }
              type="delete"
              setIsOpen={() => handleToggleModalDelete()}
              footer={
                <div className="delete-product-modal-btn-wrapper">
                  <Button
                    onClick={() => handleToggleModalDelete()}
                    label="Cancel"
                    type="secondary"
                    size="md"
                  />
                  <Button
                    onClick={() => deleteProduct()}
                    label="Delete"
                    type="warning"
                    size="md"
                  />
                </div>
              }
            />
          </div>
        )}
      </div>
      <Toaster />
    </>
  );
};

export default TableSection;
